import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from './DashboardLayout';
import Select from 'react-select';
import ConfirmationModal from './ConfirmationModal';
import { useSuccessAlert, useErrorAlert, useWarningAlert } from './AlertSystem';
import { adminApi } from '../lib/api';
import { extractErrorMessage } from '../lib/utils';
import { useAuth } from '../lib/AuthContext';
import './AdminAlertsManagement.css';

interface Alert {
    id: number;
    title: string;
    description: string;
    message?: string;
    type: 'info' | 'warning' | 'error' | 'success' | 'critical';
    priority: 'critical' | 'high' | 'medium' | 'low';
    status: 'active' | 'acknowledged' | 'investigating' | 'resolved' | 'dismissed' | 'escalated';
    category: 'security' | 'threat' | 'performance' | 'network' | 'system' | 'application' | 'compliance';
    source?: string;
    created_at: string;
    updated_at: string;
    created_by_name?: string;
    assigned_to_name?: string;
    user_id?: number;
    assigned_to?: number;
    alert_data?: any;
    resolution_notes?: string;
}

const AdminAlertsManagement: React.FC = () => {
    // Auth context
    const { hasPermission, isSuperAdmin } = useAuth();
    
    // Alert system hooks
    const showSuccess = useSuccessAlert();
    const showError = useErrorAlert();
    const showWarning = useWarningAlert();

    const [allAlerts, setAllAlerts] = useState<Alert[]>([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [selectedAlerts, setSelectedAlerts] = useState<number[]>([]);
    const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [alertToDelete, setAlertToDelete] = useState<Alert | null>(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        message: '',
        type: 'info' as Alert['type'],
        category: 'system' as Alert['category'],
        priority: 'medium' as Alert['priority'],
        status: 'active' as Alert['status'],
        source: ''
    });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState<string | null>(null);
    const alertsPerPage = 10;

    // React-select options
    const priorityOptions = [
        { value: 'all', label: 'All Priorities' },
        { value: 'critical', label: 'Critical' },
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' }
    ];

    const statusOptions = [
        { value: 'all', label: 'All Statuses' },
        { value: 'active', label: 'Active' },
        { value: 'acknowledged', label: 'Acknowledged' },
        { value: 'investigating', label: 'Investigating' },
        { value: 'resolved', label: 'Resolved' },
        { value: 'dismissed', label: 'Dismissed' },
        { value: 'escalated', label: 'Escalated' }
    ];

    const categoryOptions = [
        { value: 'all', label: 'All Categories' },
        { value: 'security', label: 'Security' },
        { value: 'threat', label: 'Threat' },
        { value: 'performance', label: 'Performance' },
        { value: 'network', label: 'Network' },
        { value: 'system', label: 'System' },
        { value: 'application', label: 'Application' },
        { value: 'compliance', label: 'Compliance' }
    ];

    const typeOptions = [
        { value: 'info', label: 'Info' },
        { value: 'warning', label: 'Warning' },
        { value: 'error', label: 'Error' },
        { value: 'success', label: 'Success' },
        { value: 'critical', label: 'Critical' }
    ];

    // Custom styles for react-select
    const selectStyles = {
        control: (provided: any, state: any) => ({
            ...provided,
            background: 'rgba(26, 26, 39, 0.8)',
            border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(148, 163, 184, 0.2)'}`,
            borderRadius: '8px',
            padding: '2px 4px',
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif",
            minHeight: '40px',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.15)' : 'none',
            cursor: 'pointer',
            '&:hover': {
                border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(99, 102, 241, 0.4)'}`
            }
        }),
        menu: (provided: any) => ({
            ...provided,
            background: 'rgba(26, 26, 39, 0.95)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            zIndex: 99999999,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)'
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            background: state.isSelected 
                ? 'rgba(99, 102, 241, 0.2)' 
                : state.isFocused 
                ? 'rgba(255, 255, 255, 0.08)' 
                : 'transparent',
            color: state.isSelected ? 'var(--primary-purple)' : 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif",
            padding: '10px 14px',
            cursor: 'pointer',
            '&:hover': {
                background: state.isSelected ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.08)'
            }
        }),
        singleValue: (provided: any) => ({
            ...provided,
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        placeholder: (provided: any) => ({
            ...provided,
            color: 'var(--text-muted)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        input: (provided: any) => ({
            ...provided,
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        dropdownIndicator: (provided: any) => ({
            ...provided,
            color: 'var(--text-muted)',
            padding: '6px',
            '&:hover': {
                color: 'var(--text-primary)'
            }
        }),
        indicatorSeparator: () => ({
            display: 'none'
        })
    };

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Client-side filtering for better responsiveness
    const filteredAlerts = useMemo(() => {
        let filtered = allAlerts;

        // Apply search filter
        if (debouncedSearchQuery.trim()) {
            const query = debouncedSearchQuery.toLowerCase();
            filtered = filtered.filter(alert =>
                alert.title.toLowerCase().includes(query) ||
                alert.description?.toLowerCase().includes(query) ||
                alert.source?.toLowerCase().includes(query) ||
                alert.message?.toLowerCase().includes(query)
            );
        }

        // Apply priority filter
        if (priorityFilter !== 'all') {
            filtered = filtered.filter(alert => alert.priority === priorityFilter);
        }

        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(alert => alert.status === statusFilter);
        }

        // Apply category filter
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(alert => alert.category === categoryFilter);
        }

        return filtered;
    }, [allAlerts, debouncedSearchQuery, priorityFilter, statusFilter, categoryFilter]);

    // Pagination
    const totalPages = Math.ceil(filteredAlerts.length / alertsPerPage);
    const startIndex = (currentPage - 1) * alertsPerPage;
    const paginatedAlerts = filteredAlerts.slice(startIndex, startIndex + alertsPerPage);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchQuery, priorityFilter, statusFilter, categoryFilter]);

    // Initial data fetch
    useEffect(() => {
        const fetchAlerts = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await adminApi.getAlerts({ limit: 1000 }); // Fetch all alerts for client-side filtering

                if (response.success && response.data) {
                    setAllAlerts(response.data.alerts || []);
                } else {
                    throw new Error(extractErrorMessage(response));
                }
            } catch (error) {
                console.error('Error fetching alerts:', error);
                setError(error instanceof Error ? error.message : 'Failed to fetch alerts');
                setAllAlerts([]);
            } finally {
                setLoading(false);
            }
        };

        fetchAlerts();
    }, []); // Only fetch once on mount

    // Optimistic update helper
    const updateAlertOptimistically = useCallback((alertId: number, updates: Partial<Alert>) => {
        setAllAlerts(prev => prev.map(alert => 
            alert.id === alertId ? { ...alert, ...updates } : alert
        ));
    }, []);

    // Add new alert optimistically
    const addAlertOptimistically = useCallback((newAlert: Alert) => {
        setAllAlerts(prev => [newAlert, ...prev]);
    }, []);

    // Remove alert optimistically
    const removeAlertOptimistically = useCallback((alertId: number) => {
        setAllAlerts(prev => prev.filter(alert => alert.id !== alertId));
    }, []);


    const formatDate = (dateString: string) => {
        if (!dateString) return 'Never';
        
        const date = new Date(dateString);
        // Ensure we're displaying in the user's local timezone
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short'
        });
    };

    const getTimeAgo = (dateString: string) => {
        if (!dateString) return 'Never';
        
        const date = new Date(dateString);
        const now = new Date();
        
        // Calculate difference in UTC to avoid timezone issues
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (diffInMinutes < 1) {
            return 'Just now';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes}m ago`;
        } else if (diffInHours < 24) {
            return `${diffInHours}h ago`;
        } else if (diffInDays < 7) {
            return `${diffInDays}d ago`;
        } else {
            return formatDate(dateString);
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'critical': return '#ef4444';
            case 'high': return '#f59e0b';
            case 'medium': return '#3b82f6';
            case 'low': return '#10b981';
            default: return '#6b7280';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active': return '#ef4444';
            case 'investigating': return '#f59e0b';
            case 'resolved': return '#10b981';
            case 'dismissed': return '#6b7280';
            default: return '#6b7280';
        }
    };

    const getCategoryIcon = (category: string) => {
        switch (category) {
            case 'security': return 'ri-shield-line';
            case 'threat': return 'ri-alarm-warning-line';
            case 'performance': return 'ri-speed-line';
            case 'network': return 'ri-wifi-line';
            case 'system': return 'ri-server-line';
            case 'application': return 'ri-code-line';
            case 'compliance': return 'ri-file-shield-line';
            default: return 'ri-alert-line';
        }
    };

    const handleSelectAlert = (alertId: number) => {
        setSelectedAlerts(prev =>
            prev.includes(alertId)
                ? prev.filter(id => id !== alertId)
                : [...prev, alertId]
        );
    };

    const handleSelectAll = () => {
        if (selectedAlerts.length === paginatedAlerts.length) {
            setSelectedAlerts([]);
        } else {
            setSelectedAlerts(paginatedAlerts.map(alert => alert.id));
        }
    };

    const handleBulkStatusChange = async (newStatus: string) => {
        if (!newStatus || selectedAlerts.length === 0) return;

        setUpdating(true);
        const selectedCount = selectedAlerts.length;
        
        // Optimistic update
        const originalAlerts = [...allAlerts];
        selectedAlerts.forEach(alertId => {
            updateAlertOptimistically(alertId, { status: newStatus as Alert['status'] });
        });

        try {
            // Use bulk API instead of individual calls
            const result = await adminApi.bulkUpdateAlerts(selectedAlerts, 'update_status', {
                status: newStatus
            });

            if (!result.success) {
                // Revert optimistic update on failure
                setAllAlerts(originalAlerts);
                throw new Error(result.error || 'Bulk update failed');
            }

            setSelectedAlerts([]);
            showSuccess(
                `Successfully updated ${selectedCount} alert${selectedCount > 1 ? 's' : ''} to ${newStatus}`,
                'Bulk Update Complete'
            );
        } catch (error) {
            console.error('Error updating alerts:', error);
            setError(error instanceof Error ? error.message : 'Failed to update alerts');
            showError(
                error instanceof Error ? error.message : 'Failed to update alerts',
                'Bulk Update Failed'
            );
            // Revert optimistic update on error
            setAllAlerts(originalAlerts);
        } finally {
            setUpdating(false);
        }
    };

    const handleDeleteAlert = async (alert: Alert) => {
        setAlertToDelete(alert);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteAlert = async () => {
        if (!alertToDelete) return;

        setUpdating(true);
        
        // Optimistic update
        removeAlertOptimistically(alertToDelete.id);

        try {
            const response = await adminApi.deleteAlert(alertToDelete.id.toString());

            if (!response.success) {
                // Revert optimistic update on failure
                setAllAlerts(prev => [alertToDelete, ...prev]);
                throw new Error(extractErrorMessage(response));
            }
            
            showSuccess(`Alert "${alertToDelete.title}" has been deleted successfully`, 'Alert Deleted');
        } catch (error) {
            console.error('Error deleting alert:', error);
            setError(error instanceof Error ? error.message : 'Failed to delete alert');
            showError(
                error instanceof Error ? error.message : 'Failed to delete alert',
                'Error Deleting Alert'
            );
            // Revert optimistic update on error
            setAllAlerts(prev => [alertToDelete, ...prev]);
        } finally {
            setUpdating(false);
            setIsDeleteModalOpen(false);
            setAlertToDelete(null);
        }
    };

    const handleEditAlert = (alert: Alert) => {
        setSelectedAlert(alert);
        setFormData({
            title: alert.title,
            description: alert.description || '',
            message: alert.message || '',
            type: alert.type,
            category: alert.category,
            priority: alert.priority,
            status: alert.status,
            source: alert.source || ''
        });
        setFormErrors({});
        setIsEditModalOpen(true);
    };

    const handleCreateAlert = () => {
        setSelectedAlert(null);
        setFormData({
            title: '',
            description: '',
            message: '',
            type: 'info',
            category: 'system',
            priority: 'medium',
            status: 'active',
            source: ''
        });
        setFormErrors({});
        setIsCreateModalOpen(true);
    };

    const validateForm = () => {
        const errors: Record<string, string> = {};

        if (!formData.title.trim()) {
            errors.title = 'Title is required';
        } else if (formData.title.length > 400) {
            errors.title = 'Title must be less than 400 characters';
        }

        if (formData.description && formData.description.length > 1000) {
            errors.description = 'Description must be less than 1000 characters';
        }

        setFormErrors(errors);
        
        if (Object.keys(errors).length > 0) {
            showWarning('Please fix the form errors before submitting', 'Form Validation Failed');
            return false;
        }
        
        return true;
    };

    const handleSubmitForm = async (isEdit: boolean) => {
        if (!validateForm()) return;

        setIsSubmitting(true);
        
        try {
            if (isEdit && selectedAlert) {
                // Optimistic update for edit
                updateAlertOptimistically(selectedAlert.id, formData);
                
                const response = await adminApi.updateAlert(selectedAlert.id.toString(), formData);
                
                if (!response.success) {
                    // Revert optimistic update on failure
                    updateAlertOptimistically(selectedAlert.id, selectedAlert);
                    throw new Error(extractErrorMessage(response));
                }
                
                showSuccess(`Alert "${formData.title}" has been updated successfully`, 'Alert Updated');
            } else {
                // Create new alert - don't do optimistic update, wait for server response
                const response = await adminApi.createAlert(formData);
                
                if (response.success && response.data && response.data.alert) {
                    // Use the complete alert object returned from the API
                    const alertData = response.data.alert;
                    const newAlert: Alert = {
                        id: alertData.id,
                        title: alertData.title,
                        description: alertData.description,
                        message: alertData.message,
                        type: alertData.type,
                        category: alertData.category,
                        priority: alertData.priority,
                        status: alertData.status,
                        source: alertData.source,
                        created_at: alertData.created_at,
                        updated_at: alertData.updated_at,
                        created_by_name: alertData.created_by_name,
                        user_id: alertData.user_id,
                        assigned_to: alertData.assigned_to,
                        assigned_to_name: alertData.assigned_to_name,
                        alert_data: alertData.alert_data,
                        resolution_notes: alertData.resolution_notes
                    };
                    
                    addAlertOptimistically(newAlert);
                    setCurrentPage(1); // Go to first page to see new alert
                    showSuccess(`Alert "${alertData.title}" has been created successfully`, 'Alert Created');
                } else {
                    console.error('Failed condition check:', {
                        success: response.success,
                        hasData: !!response.data,
                        hasAlert: !!(response.data && response.data.alert),
                        fullResponse: response
                    });
                    throw new Error(extractErrorMessage(response));
                }
            }

            // Close modals
            setIsCreateModalOpen(false);
            setIsEditModalOpen(false);
            setSelectedAlert(null);
            
            // Reset form
            setFormData({
                title: '',
                description: '',
                message: '',
                type: 'info',
                category: 'system',
                priority: 'medium',
                status: 'active',
                source: ''
            });
            setFormErrors({});
        } catch (error) {
            console.error(`Error ${isEdit ? 'updating' : 'creating'} alert:`, error);
            setError(error instanceof Error ? error.message : `Failed to ${isEdit ? 'update' : 'create'} alert`);
            showError(
                error instanceof Error ? error.message : `Failed to ${isEdit ? 'update' : 'create'} alert`,
                `Error ${isEdit ? 'Updating' : 'Creating'} Alert`
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout title="Alerts Management">
                <div className="admin-alerts-loading">
                    <div className="admin-alerts-loading-spinner">
                        <i className="ri-loader-4-line"></i>
                    </div>
                    <div className="admin-alerts-loading-text">Loading alerts...</div>
                </div>
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout title="Alerts Management">
                <div className="admin-alerts-error">
                    <div className="admin-alerts-error-icon">
                        <i className="ri-error-warning-line"></i>
                    </div>
                    <div className="admin-alerts-error-content">
                        <h3>Failed to Load Alerts</h3>
                        <p>{error}</p>
                        <button
                            className="admin-alerts-retry-btn"
                            onClick={() => window.location.reload()}
                        >
                            <i className="ri-refresh-line"></i>
                            Try Again
                        </button>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout title="Alerts Management">
            <div className="admin-alerts">
                {/* Header */}
                <div className="admin-alerts-header">
                    <div className="admin-alerts-header-content">
                        <h1 className="admin-alerts-title">Alerts Management</h1>
                        <p className="admin-alerts-subtitle">
                            Monitor, manage, and respond to system alerts and incidents across your infrastructure
                        </p>
                    </div>
                    <div className="admin-alerts-stats">
                        <div className="admin-alerts-stat">
                            <div className="admin-alerts-stat-value">{allAlerts.length}</div>
                            <div className="admin-alerts-stat-label">Total</div>
                        </div>
                        <div className="admin-alerts-stat">
                            <div className="admin-alerts-stat-value">
                                {allAlerts.filter((a: Alert) => a.status === 'active').length}
                            </div>
                            <div className="admin-alerts-stat-label">Active</div>
                        </div>
                        <div className="admin-alerts-stat">
                            <div className="admin-alerts-stat-value">
                                {allAlerts.filter((a: Alert) => a.priority === 'critical').length}
                            </div>
                            <div className="admin-alerts-stat-label">Critical</div>
                        </div>
                    </div>
                </div>

                {/* Filters & Actions */}
                <div className={`admin-alerts-filters ${updating ? 'updating' : ''}`}>
                    <div className="admin-alerts-filters-row">
                        <div className="admin-alerts-search-container">
                            <div className="admin-alerts-search">
                                <i className="ri-search-line"></i>
                                <input
                                    type="text"
                                    placeholder="Search alerts by title, description, or source..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className={`admin-alerts-search-input ${debouncedSearchQuery !== searchQuery ? 'searching' : ''}`}
                                />
                                {searchQuery && (
                                    <button
                                        className="admin-alerts-search-clear"
                                        onClick={() => setSearchQuery('')}
                                        title="Clear search"
                                    >
                                        <i className="ri-close-line"></i>
                                    </button>
                                )}
                                {debouncedSearchQuery !== searchQuery && (
                                    <div className="admin-alerts-search-loading">
                                        <i className="ri-loader-4-line rotating"></i>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="admin-alerts-actions">
                            {(hasPermission('alerts_create') || isSuperAdmin()) && (
                                <button
                                    className="admin-alerts-create-btn"
                                    onClick={handleCreateAlert}
                                >
                                    <i className="ri-add-line"></i>
                                    <span>New Alert</span>
                                </button>
                            )}
                        </div>
                    </div>
                    
                    <div className="admin-alerts-filters-secondary">
                        <div className="admin-alerts-filter-group">
                            <div className="admin-alerts-filter-item">
                                <label className="admin-alerts-filter-label">Priority</label>
                                <Select
                                    options={priorityOptions}
                                    value={priorityOptions.find(option => option.value === priorityFilter)}
                                    onChange={(option) => setPriorityFilter(option?.value || 'all')}
                                    placeholder="All"
                                    styles={selectStyles}
                                    isSearchable={false}
                                    className="admin-alerts-filter-select"
                                    isDisabled={updating}
                                />
                            </div>
                            
                            <div className="admin-alerts-filter-item">
                                <label className="admin-alerts-filter-label">Status</label>
                                <Select
                                    options={statusOptions}
                                    value={statusOptions.find(option => option.value === statusFilter)}
                                    onChange={(option) => setStatusFilter(option?.value || 'all')}
                                    placeholder="All"
                                    styles={selectStyles}
                                    isSearchable={false}
                                    className="admin-alerts-filter-select"
                                    isDisabled={updating}
                                />
                            </div>
                            
                            <div className="admin-alerts-filter-item">
                                <label className="admin-alerts-filter-label">Category</label>
                                <Select
                                    options={categoryOptions}
                                    value={categoryOptions.find(option => option.value === categoryFilter)}
                                    onChange={(option) => setCategoryFilter(option?.value || 'all')}
                                    placeholder="All"
                                    styles={selectStyles}
                                    isSearchable={false}
                                    className="admin-alerts-filter-select"
                                    isDisabled={updating}
                                />
                            </div>
                            
                            <div className="admin-alerts-filter-actions">
                                <button
                                    className="admin-alerts-filter-clear"
                                    onClick={() => {
                                        setSearchQuery('');
                                        setPriorityFilter('all');
                                        setStatusFilter('all');
                                        setCategoryFilter('all');
                                    }}
                                    title="Clear all filters"
                                    disabled={updating}
                                >
                                    <i className="ri-filter-off-line"></i>
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bulk Actions */}
                {selectedAlerts.length > 0 && (
                    <div className="admin-alerts-bulk-actions">
                        <div className="admin-alerts-bulk-info">
                            <span>{selectedAlerts.length} alert(s) selected</span>
                        </div>
                        <div className="admin-alerts-bulk-buttons">
                            <Select
                                options={[
                                    { value: 'active', label: 'Mark Active' },
                                    { value: 'investigating', label: 'Mark Investigating' },
                                    { value: 'resolved', label: 'Mark Resolved' },
                                    { value: 'dismissed', label: 'Mark Dismissed' }
                                ]}
                                value={null}
                                onChange={(option) => handleBulkStatusChange(option?.value || '')}
                                placeholder="Change Status"
                                styles={selectStyles}
                                isSearchable={false}
                            />
                            <button
                                className="admin-alerts-bulk-btn admin-alerts-bulk-clear"
                                onClick={() => {
                                    setSelectedAlerts([]);
                                }}
                            >
                                <i className="ri-close-line"></i>
                                Clear Selection
                            </button>
                        </div>
                    </div>
                )}

                {/* Alerts List */}
                <div className="admin-alerts-list-container">
                    {updating && (
                        <div className="admin-alerts-updating-indicator">
                            <i className="ri-loader-4-line rotating"></i>
                            <span>Updating alerts...</span>
                        </div>
                    )}
                    {paginatedAlerts.length === 0 ? (
                        <div className="admin-alerts-empty">
                            <i className="ri-alert-line"></i>
                            <h3>No alerts found</h3>
                            <p>Try adjusting your search or filter criteria</p>
                        </div>
                    ) : (
                        <div className={`admin-alerts-list ${updating ? 'updating' : ''}`}>
                            <div className="admin-alerts-list-header">
                                <label className="admin-alerts-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={selectedAlerts.length === paginatedAlerts.length}
                                        onChange={handleSelectAll}
                                    />
                                    <div className="admin-alerts-checkbox-checkmark">
                                        <i className="ri-check-line"></i>
                                    </div>
                                </label>
                                <div className="admin-alerts-list-header-title">Alert</div>
                                <div className="admin-alerts-list-header-title">Severity</div>
                                <div className="admin-alerts-list-header-title">Status</div>
                                <div className="admin-alerts-list-header-title">Category</div>
                                <div className="admin-alerts-list-header-title">Created</div>
                                <div className="admin-alerts-list-header-title">Actions</div>
                            </div>
                            {paginatedAlerts.map((alert) => (
                                <div key={alert.id} className="admin-alert-card">
                                    <label className="admin-alerts-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedAlerts.includes(alert.id)}
                                            onChange={() => handleSelectAlert(alert.id)}
                                        />
                                        <div className="admin-alerts-checkbox-checkmark">
                                            <i className="ri-check-line"></i>
                                        </div>
                                    </label>
                                    <div className="admin-alert-main">
                                        <div className="admin-alert-icon">
                                            <i className={getCategoryIcon(alert.category)}></i>
                                        </div>
                                        <div className="admin-alert-content">
                                            <h4 className="admin-alert-title">{alert.title}</h4>
                                            <p className="admin-alert-description">{alert.description}</p>
                                            <div className="admin-alert-meta">
                                                <span className="admin-alert-source">
                                                    <i className="ri-server-line"></i>
                                                    {alert.source || 'System'}
                                                </span>
                                                {alert.created_by_name && (
                                                    <span className="admin-alert-assigned">
                                                        <i className="ri-user-line"></i>
                                                        {alert.created_by_name}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="admin-alert-severity">
                                        <span
                                            className="admin-alert-severity-badge"
                                            style={{
                                                backgroundColor: `${getPriorityColor(alert.priority)}20`,
                                                color: getPriorityColor(alert.priority)
                                            }}
                                        >
                                            {alert.priority}
                                        </span>
                                    </div>
                                    <div className="admin-alert-status">
                                        <span
                                            className="admin-alert-status-badge"
                                            style={{
                                                backgroundColor: `${getStatusColor(alert.status)}20`,
                                                color: getStatusColor(alert.status)
                                            }}
                                        >
                                            {alert.status}
                                        </span>
                                    </div>
                                    <div className="admin-alert-category">
                                        <span className="admin-alert-category-badge">
                                            {alert.category}
                                        </span>
                                    </div>
                                    <div className="admin-alert-time">
                                        <span className="admin-alert-time-main">{getTimeAgo(alert.created_at)}</span>
                                        <span className="admin-alert-time-full">{formatDate(alert.created_at)}</span>
                                    </div>
                                    <div className="admin-alert-actions">
                                        {(hasPermission('alerts_edit') || isSuperAdmin()) && (
                                            <button
                                                className="admin-alert-action-btn"
                                                onClick={() => handleEditAlert(alert)}
                                                title="Edit alert"
                                            >
                                                <i className="ri-edit-line"></i>
                                            </button>
                                        )}
                                        {(hasPermission('alerts_delete') || isSuperAdmin()) && (
                                            <button
                                                className="admin-alert-action-btn admin-alert-delete-btn"
                                                onClick={() => handleDeleteAlert(alert)}
                                                title="Delete alert"
                                            >
                                                <i className="ri-delete-bin-line"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className={`admin-alerts-pagination ${updating ? 'updating' : ''}`}>
                        <button
                            className="admin-alerts-pagination-btn"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1 || updating}
                        >
                            <i className="ri-arrow-left-line"></i>
                            Previous
                        </button>
                        <div className="admin-alerts-pagination-info">
                            Page {currentPage} of {totalPages} ({filteredAlerts.length} alerts)
                        </div>
                        <button
                            className="admin-alerts-pagination-btn"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages || updating}
                        >
                            Next
                            <i className="ri-arrow-right-line"></i>
                        </button>
                    </div>
                )}

                {isCreateModalOpen && (
                    <div className="admin-alerts-modal-overlay" onClick={() => setIsCreateModalOpen(false)}>
                        <div className="admin-alerts-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="admin-alerts-modal-header">
                                <div className="admin-alerts-modal-header-content">
                                    <h3>Create New Alert</h3>
                                    <p>Add a new system alert to notify users about important incidents or information</p>
                                </div>
                                <button onClick={() => setIsCreateModalOpen(false)}>
                                    <i className="ri-close-line"></i>
                                </button>
                            </div>
                            <div className="admin-alerts-modal-content">
                                <form onSubmit={(e) => { e.preventDefault(); handleSubmitForm(false); }}>
                                    <div className="admin-alerts-form-section">
                                        <h4>Alert Details</h4>
                                        
                                        <div className="admin-alerts-form-group">
                                            <label>Title *</label>
                                            <input
                                                type="text"
                                                value={formData.title}
                                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                                placeholder="Enter a clear, descriptive alert title"
                                                className={formErrors.title ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.title && <span className="error-text">{formErrors.title}</span>}
                                        </div>

                                        <div className="admin-alerts-form-group">
                                            <label>Description</label>
                                            <textarea
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                placeholder="Brief description of the alert"
                                                rows={2}
                                                className={formErrors.description ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.description && <span className="error-text">{formErrors.description}</span>}
                                        </div>

                                        <div className="admin-alerts-form-group">
                                            <label>Detailed Message</label>
                                            <textarea
                                                value={formData.message}
                                                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                                placeholder="Detailed message with resolution steps or additional information"
                                                rows={4}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>

                                    <div className="admin-alerts-form-section">
                                        <h4>Classification</h4>
                                        
                                        <div className="admin-alerts-form-grid">
                                            <div className="admin-alerts-form-group">
                                                <label>Type</label>
                                                <Select
                                                    value={typeOptions.find(option => option.value === formData.type)}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, type: selectedOption.value })}
                                                    options={typeOptions}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select type"
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Priority</label>
                                                <Select
                                                    value={priorityOptions.slice(1).find(option => option.value === formData.priority)}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, priority: selectedOption.value })}
                                                    options={priorityOptions.slice(1)}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select priority"
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Category</label>
                                                <Select
                                                    value={categoryOptions.slice(1).find(option => option.value === formData.category)}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, category: selectedOption.value })}
                                                    options={categoryOptions.slice(1)}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select category"
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Initial Status</label>
                                                <Select
                                                    value={statusOptions.slice(1).find(option => option.value === formData.status)}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, status: selectedOption.value })}
                                                    options={statusOptions.slice(1)}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select status"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="admin-alerts-form-section">
                                        <h4>Additional Information</h4>
                                        
                                        <div className="admin-alerts-form-group">
                                            <label>Source</label>
                                            <input
                                                type="text"
                                                value={formData.source}
                                                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                                                placeholder="Alert source (e.g., Monitoring System, Security Scanner, Manual)"
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>

                                    <div className="admin-alerts-modal-actions">
                                        <button
                                            type="button"
                                            onClick={() => setIsCreateModalOpen(false)}
                                            disabled={isSubmitting}
                                            className="admin-alerts-btn-secondary"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="admin-alerts-btn-primary"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <i className="ri-loader-4-line rotating"></i>
                                                    Creating Alert...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-add-line"></i>
                                                    Create Alert
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {isEditModalOpen && selectedAlert && (
                    <div className="admin-alerts-modal-overlay" onClick={() => setIsEditModalOpen(false)}>
                        <div className="admin-alerts-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="admin-alerts-modal-header">
                                <div className="admin-alerts-modal-header-content">
                                    <h3>Edit Alert</h3>
                                    <p>Update alert information and status</p>
                                </div>
                                <button onClick={() => setIsEditModalOpen(false)}>
                                    <i className="ri-close-line"></i>
                                </button>
                            </div>
                            <div className="admin-alerts-modal-content">
                                <form onSubmit={(e) => { e.preventDefault(); handleSubmitForm(true); }}>
                                    <div className="admin-alerts-form-section">
                                        <h4>Alert Details</h4>
                                        
                                        <div className="admin-alerts-form-group">
                                            <label>Title *</label>
                                            <input
                                                type="text"
                                                value={formData.title}
                                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                                placeholder="Enter a clear, descriptive alert title"
                                                className={formErrors.title ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.title && <span className="error-text">{formErrors.title}</span>}
                                        </div>

                                        <div className="admin-alerts-form-group">
                                            <label>Description</label>
                                            <textarea
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                placeholder="Brief description of the alert"
                                                rows={2}
                                                className={formErrors.description ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {formErrors.description && <span className="error-text">{formErrors.description}</span>}
                                        </div>

                                        <div className="admin-alerts-form-group">
                                            <label>Detailed Message</label>
                                            <textarea
                                                value={formData.message}
                                                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                                placeholder="Detailed message with resolution steps or additional information"
                                                rows={4}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>

                                    <div className="admin-alerts-form-section">
                                        <h4>Classification</h4>
                                        
                                        <div className="admin-alerts-form-grid">
                                            <div className="admin-alerts-form-group">
                                                <label>Type</label>
                                                <Select
                                                    value={typeOptions.find(option => option.value === formData.type)}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, type: selectedOption.value })}
                                                    options={typeOptions}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select type"
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Priority</label>
                                                <Select
                                                    value={priorityOptions.slice(1).find(option => option.value === formData.priority)}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, priority: selectedOption.value })}
                                                    options={priorityOptions.slice(1)}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select priority"
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Category</label>
                                                <Select
                                                    value={categoryOptions.slice(1).find(option => option.value === formData.category)}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, category: selectedOption.value })}
                                                    options={categoryOptions.slice(1)}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select category"
                                                />
                                            </div>

                                            <div className="admin-alerts-form-group">
                                                <label>Status</label>
                                                <Select
                                                    value={statusOptions.slice(1).find(option => option.value === formData.status)}
                                                    onChange={(selectedOption: any) => setFormData({ ...formData, status: selectedOption.value })}
                                                    options={statusOptions.slice(1)}
                                                    styles={selectStyles}
                                                    isDisabled={isSubmitting}
                                                    placeholder="Select status"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="admin-alerts-form-section">
                                        <h4>Additional Information</h4>
                                        
                                        <div className="admin-alerts-form-group">
                                            <label>Source</label>
                                            <input
                                                type="text"
                                                value={formData.source}
                                                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                                                placeholder="Alert source (e.g., Monitoring System, Security Scanner, Manual)"
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>

                                    <div className="admin-alerts-modal-actions">
                                        <button
                                            type="button"
                                            onClick={() => setIsEditModalOpen(false)}
                                            disabled={isSubmitting}
                                            className="admin-alerts-btn-secondary"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="admin-alerts-btn-primary"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <i className="ri-loader-4-line rotating"></i>
                                                    Updating Alert...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-save-line"></i>
                                                    Update Alert
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                <ConfirmationModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={confirmDeleteAlert}
                    title="Delete Alert"
                    message="Are you sure you want to delete this alert? This action cannot be undone."
                    confirmText="Delete Alert"
                    type="danger"
                    isLoading={updating}
                >
                    {alertToDelete && (
                        <div className="admin-alerts-delete-modal-alert">
                            <div className="admin-alerts-delete-modal-alert-icon">
                                <i className={getCategoryIcon(alertToDelete.category)}></i>
                            </div>
                            <div className="admin-alerts-delete-modal-alert-info">
                                <h4>{alertToDelete.title}</h4>
                                <p>{alertToDelete.description}</p>
                                <div className="admin-alerts-delete-modal-alert-meta">
                                    <span className="admin-alerts-delete-modal-alert-priority" 
                                          style={{ color: getPriorityColor(alertToDelete.priority) }}>
                                        {alertToDelete.priority} priority
                                    </span>
                                    <span className="admin-alerts-delete-modal-alert-category">
                                        {alertToDelete.category}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </ConfirmationModal>
            </div>
        </DashboardLayout>
    );
};

export default AdminAlertsManagement;
